import React, { useEffect, useState } from "react";
import { BiX } from "react-icons/bi";
import { Audio } from "react-loader-spinner";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { AutoSizer, List } from "react-virtualized";
import { GetAllParameters, RemoveParameter } from "src/assets/functions";
import { useStudent } from "src/contexts/student";
import api from "src/rest/api";
import { CenteredDiv } from "src/screens/calendar/styling/styles";
import { ExerciseData } from "../../interfaces";
import "../../styles.css";
import ExerciseItem from "./exerciseitem";

/** Interface para Exercícios */
export interface Exercise {
  exercise_name: string;
  exercise_data: ExerciseData[];
  id: string;
}

/** Componente Principal */
const ProgressionView: React.FC<{
  header: { title: string; subtitle: string };
}> = ({ header }) => {
  const navigate = useNavigate();
  const { student } = useStudent();
  const { plan, workout, exc } = GetAllParameters();

  const [filter, setFilter] = useState<string | undefined>(exc);

  useEffect(() => {
    setFilter(exc);
  }, [exc]);

  const {
    data: exercises,
    isLoading,
    isError,
  } = useQuery(["PVIEW_", plan, workout], () =>
    api
      .post<Exercise[]>("/cprogress/view", {
        userid: student?.id,
        plan,
        workout,
      })
      .then((res) => res.data),
  );

  const removeFilter = () => {
    navigate(RemoveParameter("exc"), { replace: true });
  };
  if (isLoading) {
    return (
      <CenteredDiv>
        <Audio color="black" height={40} width={40} />
      </CenteredDiv>
    );
  } else if (isError || !exercises) {
    return (
      <div className="m-auto">
        <p className="font-semibold text-red-500 dark:text-red-400">
          Erro ao carregar os dados!
        </p>
      </div>
    );
  } else {
    const filteredExercises = exercises.filter(
      (e) => !filter || e.id === filter,
    );

    return (
      <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-white p-4 dark:bg-zinc-900">
        <header className="flex flex-col pb-2">
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
            {header.title}
          </h1>
          <h2 className="text-zinc-700 dark:text-zinc-300">
            {header.subtitle}
          </h2>
        </header>

        {filter && (
          <div className="flex-row items-center gap-4 pb-2 text-xs">
            <p className="inline text-zinc-700 dark:text-zinc-300">Filtro: </p>
            <button
              onClick={removeFilter}
              className="flex items-center gap-2 rounded-full border border-zinc-500 px-2 py-1 transition hover:bg-zinc-100 dark:border-zinc-400 dark:hover:bg-zinc-800"
              aria-label="Remover filtro"
            >
              <BiX size={20} className="text-zinc-700 dark:text-zinc-300" />
              <p className="text-xs text-zinc-700 dark:text-zinc-300">
                {exercises.find((e) => e.id === filter)?.exercise_name ??
                  filter}
              </p>
            </button>
          </div>
        )}

        {filteredExercises.length === 0 ? (
          <p className="text-center text-zinc-500 dark:text-zinc-400">
            Nenhum exercício encontrado.
          </p>
        ) : (
          <div className="block h-full w-full">
            <AutoSizer style={{ width: "100%" }}>
              {({ height, width }) => {
                return (
                  <List
                    height={height}
                    width={width}
                    rowCount={filteredExercises.length}
                    rowHeight={300}
                    rowRenderer={({ index, key, style, isVisible }) => (
                      <ExerciseItem
                        key={plan + "_" + workout + "_" + index}
                        style={style}
                        index={index}
                        exercise={filteredExercises[index]}
                        isLoading={!isVisible}
                      />
                    )}
                  />
                );
              }}
            </AutoSizer>
          </div>
        )}
      </div>
    );
  }
};

export default ProgressionView;
